import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../api/client';
import {
  PRIMARY,
  TEXT_DARK,
  TEXT_MUTED,
  TEXT_BODY,
  BORDER_LIGHT,
  BG_PAGE,
  BG_CARD,
  BG_SURFACE,
  FONT_HEADING,
} from '../components/legacy-ball/table-management/designTokens';

// ── Types ─────────────────────────────────────────────────────────────────

interface KrioProverb {
  wisdomId: string;
  title: string;
  translation: string;
  interpretation: string;
}

interface StoryInsight {
  id: string;
  culturalThemes: string[];
  familyThemes: string[];
  heritageMarkers: string[];
  valuesExpressed: string[];
  communityInvolvement: string[];
  generationalConnections: string[];
}

interface Story {
  id: string;
  sessionId: string;
  userId: string;
  title: string;
  narrative: string;
  culturalInsight: string;
  krioProverbId: string | null;
  heritageFact: string | null;
  status: 'Generated' | 'Approved' | 'Declined' | 'Flagged' | 'Removed';
  guestName: string;
  chapterAffiliation: string | null;
  familyName: string | null;
  reunionYear: number;
  featured: boolean;
  regenerationCount: number;
  approvedAt: string | null;
  createdAt: string;
  updatedAt: string;
  insights: StoryInsight | null;
  translation: unknown | null;
  krioProverb?: KrioProverb | null;
}

type ViewState = 'loading' | 'display' | 'edit' | 'success' | 'declined';

// ── Component ─────────────────────────────────────────────────────────────

export function StoryBuilderReviewPage() {
  const navigate = useNavigate();

  const [viewState, setViewState] = useState<ViewState>('loading');
  const [story, setStory] = useState<Story | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Edit mode state
  const [editNarrative, setEditNarrative] = useState('');
  const [editTitle, setEditTitle] = useState('');
  const [editError, setEditError] = useState<string | null>(null);

  // Fetch story on mount
  const fetchStory = useCallback(async () => {
    try {
      setError(null);
      setViewState('loading');
      const data = await apiClient<Story>('/story-builder/stories/mine');

      if (!data || data.status !== 'Generated') {
        // If story is already approved, redirect to my-story
        if (data?.status === 'Approved') {
          navigate('/story-builder/my-story', { replace: true });
          return;
        }
        // If story is declined or flagged, show it anyway so user can see it
        // (don't redirect to interview — that creates a loop since session is story_generated)
        if (data && (data.status === 'Declined' || data.status === 'Flagged' || data.status === 'Removed')) {
          setStory(data);
          setEditNarrative(data.narrative);
          setEditTitle(data.title);
          setViewState('display');
          return;
        }
        // No story at all — redirect to interview
        navigate('/story-builder/interview', { replace: true });
        return;
      }

      setStory(data);
      setEditNarrative(data.narrative);
      setEditTitle(data.title);
      setViewState('display');
    } catch (err: any) {
      if (err?.statusCode === 404) {
        navigate('/story-builder/interview', { replace: true });
        return;
      }
      setError('Failed to load your story. Please try again.');
      setViewState('display');
    }
  }, [navigate]);

  useEffect(() => {
    fetchStory();
  }, [fetchStory]);

  // ── Actions ───────────────────────────────────────────────────────────

  const handleApprove = async () => {
    if (!story) return;
    setActionLoading(true);
    setError(null);
    try {
      await apiClient(`/story-builder/stories/${story.id}/review`, {
        method: 'PATCH',
        body: { action: 'approve' },
      });
      setViewState('success');
    } catch (err: any) {
      setError(err?.message || 'Failed to approve story. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDecline = async () => {
    if (!story) return;
    const confirmed = window.confirm(
      'Are you sure you want to decline publication? Your story will be saved privately.',
    );
    if (!confirmed) return;

    setActionLoading(true);
    setError(null);
    try {
      await apiClient(`/story-builder/stories/${story.id}/review`, {
        method: 'PATCH',
        body: { action: 'decline' },
      });
      setViewState('declined');
    } catch (err: any) {
      setError(err?.message || 'Failed to decline story. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRegenerate = async () => {
    if (!story) return;
    setActionLoading(true);
    setError(null);
    try {
      const regenerated = await apiClient<Story>(
        `/story-builder/stories/${story.id}/regenerate`,
        { method: 'POST' },
      );
      setStory(regenerated);
      setEditNarrative(regenerated.narrative);
      setEditTitle(regenerated.title);
      setViewState('display');
    } catch (err: any) {
      setError(
        err?.message || 'Failed to regenerate story. Please try again.',
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleEnterEdit = () => {
    if (!story) return;
    setEditNarrative(story.narrative);
    setEditTitle(story.title);
    setEditError(null);
    setViewState('edit');
  };

  const handleSaveEdit = async () => {
    if (!story) return;

    // Validate
    const trimmedNarrative = editNarrative.trim();
    const trimmedTitle = editTitle.trim();

    if (trimmedNarrative.length < 1 || trimmedNarrative.length > 500) {
      setEditError('Narrative must be between 1 and 500 characters.');
      return;
    }
    if (trimmedTitle.length > 80) {
      setEditError('Title must be at most 80 characters.');
      return;
    }

    setActionLoading(true);
    setEditError(null);
    setError(null);
    try {
      const updated = await apiClient<Story>(
        `/story-builder/stories/${story.id}/review`,
        {
          method: 'PATCH',
          body: {
            action: 'edit',
            narrative: trimmedNarrative,
            title: trimmedTitle,
          },
        },
      );
      setStory(updated);
      setEditNarrative(updated.narrative);
      setEditTitle(updated.title);
      setViewState('display');
    } catch (err: any) {
      setEditError(err?.message || 'Failed to save edits. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelEdit = () => {
    if (!story) return;
    setEditNarrative(story.narrative);
    setEditTitle(story.title);
    setEditError(null);
    setViewState('display');
  };

  // ── Render: Loading ───────────────────────────────────────────────────

  if (viewState === 'loading') {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.loadingContainer}>
            <div style={styles.spinner} />
            <h2 style={{ fontFamily: FONT_HEADING, fontSize: '1.3rem', color: TEXT_DARK, margin: '1rem 0 0.5rem' }}>
              Crafting your story...
            </h2>
            <p style={styles.loadingText}>
              Our AI is weaving your responses into a personalized narrative
              celebrating your connection to Krio heritage and the KDU community.
            </p>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        </div>
      </div>
    );
  }

  // ── Render: Error state with retry ────────────────────────────────────

  if (error && !story) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.loadingContainer}>
            <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>⚠️</div>
            <h2 style={{ fontFamily: FONT_HEADING, fontSize: '1.3rem', color: '#991b1b', margin: '0 0 0.75rem' }}>
              Generation Failed
            </h2>
            <p style={{ color: TEXT_MUTED, fontSize: '0.95rem', marginBottom: '1.5rem', textAlign: 'center' as const }}>
              {error}
            </p>
            <button style={styles.btnPrimary} onClick={fetchStory}>
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Render: Success (after approval) ──────────────────────────────────

  if (viewState === 'success') {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.successContainer}>
            <div style={styles.successIcon}>✓</div>
            <h2 style={styles.successTitle}>Story Approved!</h2>
            <p style={styles.successText}>
              Your story has been published and added to the KDU Story Archive.
              It will be available for the reunion weekend displays and
              commemorative materials.
            </p>
            <button
              style={styles.btnPrimary}
              onClick={() => navigate('/story-builder/my-story')}
            >
              View My Story
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Render: Declined confirmation ─────────────────────────────────────

  if (viewState === 'declined') {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.successContainer}>
            <div style={{ ...styles.successIcon, backgroundColor: '#f3e5e5', color: '#c62828' }}>✕</div>
            <h2 style={styles.successTitle}>Story Declined</h2>
            <p style={styles.successText}>
              Your story has been saved privately and will not be published. You
              can always come back to the interview to generate a new story.
            </p>
            <button
              style={styles.btnPrimary}
              onClick={() => navigate('/story-builder/interview')}
            >
              Back to Interview
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Render: Edit Mode ─────────────────────────────────────────────────

  if (viewState === 'edit') {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <h1 style={styles.pageTitle}>Edit Your Story</h1>
          <p style={styles.pageSubtitle}>
            Modify the title and narrative text below. The narrative must be
            between 1 and 500 characters.
          </p>

          {editError && <div style={styles.errorBanner}>{editError}</div>}

          <div style={styles.formGroup}>
            <label style={styles.label} htmlFor="edit-title">
              Story Title
            </label>
            <input
              id="edit-title"
              style={styles.input}
              type="text"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              maxLength={80}
              placeholder="Enter story title (max 80 characters)"
            />
            <span style={styles.charCount}>{editTitle.length}/80</span>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label} htmlFor="edit-narrative">
              Narrative
            </label>
            <textarea
              id="edit-narrative"
              style={styles.textarea}
              value={editNarrative}
              onChange={(e) => setEditNarrative(e.target.value)}
              maxLength={500}
              rows={8}
              placeholder="Enter narrative text (1-500 characters)"
            />
            <span
              style={{
                ...styles.charCount,
                color:
                  editNarrative.length > 500 || editNarrative.trim().length < 1
                    ? '#c62828'
                    : TEXT_MUTED,
              }}
            >
              {editNarrative.length}/500
            </span>
          </div>

          <div style={styles.editActions}>
            <button
              style={styles.btnPrimary}
              onClick={handleSaveEdit}
              disabled={actionLoading}
            >
              {actionLoading ? 'Saving...' : 'Save Changes'}
            </button>
            <button
              style={styles.btnOutlined}
              onClick={handleCancelEdit}
              disabled={actionLoading}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Render: Story Display ─────────────────────────────────────────────

  const remainingRegenerations = story ? 3 - story.regenerationCount : 0;

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <h1 style={styles.pageTitle}>Review Your Story</h1>
        <p style={styles.pageSubtitle}>
          Here is your generated story. Read it carefully and choose an action
          below.
        </p>

        {error && <div style={styles.errorBanner}>{error}</div>}

        {story && (
          <>
            {/* Story Title */}
            <div style={styles.storySection}>
              <h2 style={styles.storyTitle}>{story.title}</h2>
            </div>

            {/* Narrative */}
            <div style={styles.storySection}>
              <h3 style={styles.sectionLabel}>Your Narrative</h3>
              <p style={styles.narrativeText}>{story.narrative}</p>
            </div>

            {/* Cultural Insight */}
            <div style={styles.storySection}>
              <h3 style={styles.sectionLabel}>Cultural Insight</h3>
              <p style={styles.insightText}>{story.culturalInsight}</p>
            </div>

            {/* Krio Proverb (if present) */}
            {story.krioProverb && (
              <div style={styles.proverbCard}>
                <h3 style={styles.sectionLabel}>🌍 Krio Proverb</h3>
                <p style={styles.proverbText}>{story.krioProverb.title}</p>
                <p style={styles.proverbTranslation}>
                  "{story.krioProverb.translation}"
                </p>
                {story.krioProverb.interpretation && (
                  <p style={styles.proverbInterpretation}>
                    {story.krioProverb.interpretation}
                  </p>
                )}
              </div>
            )}

            {/* Heritage Fact (if present) */}
            {story.heritageFact && (
              <div style={styles.factCard}>
                <h3 style={styles.sectionLabel}>📜 Heritage Fact</h3>
                <p style={styles.factText}>{story.heritageFact}</p>
              </div>
            )}

            {/* Action Buttons */}
            <div style={styles.actionsContainer}>
              <button
                style={styles.btnApprove}
                onClick={handleApprove}
                disabled={actionLoading}
                aria-label="Approve story for publication"
              >
                {actionLoading ? 'Processing...' : '✓ Approve for Publication'}
              </button>

              <button
                style={styles.btnOutlined}
                onClick={handleEnterEdit}
                disabled={actionLoading}
                aria-label="Edit story"
              >
                ✏️ Edit Story
              </button>

              <button
                style={{
                  ...styles.btnOutlined,
                  opacity: remainingRegenerations <= 0 ? 0.5 : 1,
                  cursor: remainingRegenerations <= 0 ? 'not-allowed' : 'pointer',
                }}
                onClick={handleRegenerate}
                disabled={actionLoading || remainingRegenerations <= 0}
                aria-label={`Regenerate story, ${remainingRegenerations} of 3 remaining`}
              >
                🔄 Regenerate ({remainingRegenerations} of 3 remaining)
              </button>

              <button
                style={styles.btnDecline}
                onClick={handleDecline}
                disabled={actionLoading}
                aria-label="Decline publication"
              >
                Decline Publication
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Styles ──────────────────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  page: {
    backgroundColor: BG_PAGE,
    minHeight: '100vh',
    padding: '2rem 1rem',
  },
  card: {
    maxWidth: '720px',
    margin: '0 auto',
    backgroundColor: BG_CARD,
    borderRadius: '12px',
    border: `1px solid ${BORDER_LIGHT}`,
    padding: '2rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  pageTitle: {
    fontFamily: FONT_HEADING,
    fontSize: '1.75rem',
    fontWeight: 700,
    color: TEXT_DARK,
    margin: '0 0 0.5rem 0',
  },
  pageSubtitle: {
    fontSize: '0.95rem',
    color: TEXT_MUTED,
    margin: '0 0 1.5rem 0',
    lineHeight: 1.5,
  },
  errorBanner: {
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '8px',
    padding: '0.75rem 1rem',
    color: '#991b1b',
    fontSize: '0.875rem',
    marginBottom: '1rem',
  },

  // Loading
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '3rem 1rem',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: `3px solid ${BORDER_LIGHT}`,
    borderTopColor: PRIMARY,
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  loadingText: {
    marginTop: '1rem',
    color: TEXT_MUTED,
    fontSize: '0.95rem',
  },

  // Success / Declined
  successContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    padding: '2rem 1rem',
  },
  successIcon: {
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    backgroundColor: '#e8f5e9',
    color: PRIMARY,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.75rem',
    fontWeight: 700,
    marginBottom: '1rem',
  },
  successTitle: {
    fontFamily: FONT_HEADING,
    fontSize: '1.5rem',
    fontWeight: 700,
    color: TEXT_DARK,
    margin: '0 0 0.75rem 0',
  },
  successText: {
    fontSize: '0.95rem',
    color: TEXT_BODY,
    lineHeight: 1.6,
    maxWidth: '480px',
    margin: '0 0 1.5rem 0',
  },

  // Story sections
  storySection: {
    marginBottom: '1.5rem',
    paddingBottom: '1.25rem',
    borderBottom: `1px solid ${BORDER_LIGHT}`,
  },
  storyTitle: {
    fontFamily: FONT_HEADING,
    fontSize: '1.35rem',
    fontWeight: 600,
    color: TEXT_DARK,
    margin: 0,
  },
  sectionLabel: {
    fontSize: '0.8rem',
    fontWeight: 600,
    color: TEXT_MUTED,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    margin: '0 0 0.5rem 0',
  },
  narrativeText: {
    fontSize: '1rem',
    color: TEXT_BODY,
    lineHeight: 1.7,
    margin: 0,
    whiteSpace: 'pre-wrap',
  },
  insightText: {
    fontSize: '0.95rem',
    color: TEXT_BODY,
    lineHeight: 1.6,
    margin: 0,
    fontStyle: 'italic',
  },

  // Proverb
  proverbCard: {
    backgroundColor: BG_SURFACE,
    borderRadius: '8px',
    padding: '1.25rem',
    marginBottom: '1.5rem',
    borderLeft: `3px solid ${PRIMARY}`,
  },
  proverbText: {
    fontSize: '1.05rem',
    fontWeight: 600,
    color: TEXT_DARK,
    margin: '0 0 0.5rem 0',
  },
  proverbTranslation: {
    fontSize: '0.9rem',
    color: TEXT_BODY,
    fontStyle: 'italic',
    margin: '0 0 0.4rem 0',
  },
  proverbInterpretation: {
    fontSize: '0.85rem',
    color: TEXT_MUTED,
    margin: 0,
  },

  // Heritage Fact
  factCard: {
    backgroundColor: '#fef9e7',
    borderRadius: '8px',
    padding: '1.25rem',
    marginBottom: '1.5rem',
    borderLeft: '3px solid #c9a84c',
  },
  factText: {
    fontSize: '0.95rem',
    color: TEXT_BODY,
    lineHeight: 1.6,
    margin: 0,
  },

  // Actions
  actionsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
    marginTop: '1.5rem',
    paddingTop: '1.5rem',
    borderTop: `1px solid ${BORDER_LIGHT}`,
  },
  btnPrimary: {
    backgroundColor: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    padding: '0.75rem 1.5rem',
    fontSize: '0.95rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'opacity 0.15s',
  },
  btnApprove: {
    backgroundColor: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    padding: '0.85rem 1.5rem',
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'opacity 0.15s',
  },
  btnOutlined: {
    backgroundColor: 'transparent',
    color: TEXT_DARK,
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '8px',
    padding: '0.75rem 1.5rem',
    fontSize: '0.95rem',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'background-color 0.15s',
  },
  btnDecline: {
    backgroundColor: 'transparent',
    color: '#c62828',
    border: 'none',
    borderRadius: '8px',
    padding: '0.75rem 1.5rem',
    fontSize: '0.9rem',
    fontWeight: 500,
    cursor: 'pointer',
    textDecoration: 'underline',
    transition: 'opacity 0.15s',
  },

  // Edit mode
  formGroup: {
    marginBottom: '1.25rem',
    position: 'relative',
  },
  label: {
    display: 'block',
    fontSize: '0.85rem',
    fontWeight: 600,
    color: TEXT_DARK,
    marginBottom: '0.4rem',
  },
  input: {
    width: '100%',
    padding: '0.6rem 0.75rem',
    fontSize: '0.95rem',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '6px',
    color: TEXT_BODY,
    boxSizing: 'border-box',
    outline: 'none',
  },
  textarea: {
    width: '100%',
    padding: '0.6rem 0.75rem',
    fontSize: '0.95rem',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '6px',
    color: TEXT_BODY,
    boxSizing: 'border-box',
    outline: 'none',
    resize: 'vertical',
    fontFamily: 'inherit',
    lineHeight: 1.6,
  },
  charCount: {
    display: 'block',
    textAlign: 'right',
    fontSize: '0.75rem',
    color: TEXT_MUTED,
    marginTop: '0.25rem',
  },
  editActions: {
    display: 'flex',
    gap: '0.75rem',
    marginTop: '1.5rem',
  },
};
